export const WEFAT_REQUEST_API_NAMES = Object.freeze(['request']);
export class WxRequestRuntimeError extends Error {
    code;
    errCode;
    errMsg;
    api = 'request';
    remediation;
    constructor(code, reason, remediation, cause) {
        const errMsg = `request:fail ${reason}`;
        super(`${code} ${errMsg}`);
        this.name = 'WxRequestRuntimeError';
        this.code = code;
        this.errCode = code;
        this.errMsg = errMsg;
        this.remediation = remediation;
        if (cause !== undefined)
            Object.defineProperty(this, 'cause', { value: cause });
    }
}
const CALLBACK_KEYS = new Set(['success', 'fail', 'complete']);
const OPTION_KEYS = new Set([
    ...CALLBACK_KEYS,
    'url',
    'data',
    'header',
    'method',
    'timeout',
    'dataType',
    'responseType',
]);
const METHODS = new Set(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS']);
function isPlainRecord(value) {
    if (value === null || typeof value !== 'object' || Array.isArray(value))
        return false;
    const prototype = Object.getPrototypeOf(value);
    return prototype === Object.prototype || prototype === null;
}
function optionError(reason) {
    return new WxRequestRuntimeError('WF6161', reason, 'Pass a direct HTTP(S) URL and only the supported request, response, and callback fields.');
}
function requestError(error) {
    if (error instanceof WxRequestRuntimeError)
        return error;
    const reason = error instanceof Error ? error.message : String(error);
    return new WxRequestRuntimeError('WF6160', reason, 'Confirm the target permits browser CORS access, the browser is online, and the request has not timed out or been aborted.', error);
}
function readOptions(raw) {
    if (!isPlainRecord(raw))
        throw optionError('options must be a plain object');
    for (const key of Reflect.ownKeys(raw)) {
        const descriptor = typeof key === 'string' ? Object.getOwnPropertyDescriptor(raw, key) : undefined;
        if (typeof key !== 'string' || !OPTION_KEYS.has(key)) {
            throw optionError(`unsupported option field ${String(key)}`);
        }
        if (descriptor === undefined || !('value' in descriptor)) {
            throw optionError(`option field ${key} must be a direct data property`);
        }
        if (CALLBACK_KEYS.has(key) && descriptor.value !== undefined && typeof descriptor.value !== 'function') {
            throw optionError(`${key} must be a function when provided`);
        }
    }
    if (typeof raw.url !== 'string' || raw.url.length === 0)
        throw optionError('url must be a non-empty string');
    const method = raw.method === undefined ? 'GET' : raw.method;
    if (typeof method !== 'string' || !METHODS.has(method.toUpperCase())) {
        throw optionError(`unsupported method ${String(method)}`);
    }
    if (raw.timeout !== undefined && (typeof raw.timeout !== 'number' || !Number.isFinite(raw.timeout) || raw.timeout < 0)) {
        throw optionError('timeout must be a finite non-negative number');
    }
    if (raw.dataType !== undefined && raw.dataType !== 'json' && raw.dataType !== 'text') {
        throw optionError('dataType must be json or text');
    }
    if (raw.responseType !== undefined && raw.responseType !== 'arraybuffer' && raw.responseType !== 'text') {
        throw optionError('responseType must be text or arraybuffer');
    }
    if (raw.header !== undefined && !isPlainRecord(raw.header)) {
        throw optionError('header must be a plain object');
    }
    return raw;
}
function browserEnvironment() {
    if (typeof window === 'undefined' || typeof window.fetch !== 'function')
        return null;
    return { baseUrl: window.location.href, fetch: window.fetch.bind(window) };
}
function requestHeaders(raw) {
    const result = new Headers();
    for (const [name, value] of Object.entries(raw ?? {})) {
        if (typeof value !== 'string' && typeof value !== 'number') {
            throw optionError(`header ${name} must be a string or number`);
        }
        result.set(name, String(value));
    }
    return result;
}
function queryValue(value) {
    if (typeof value === 'string')
        return value;
    if (value === null || typeof value === 'number' || typeof value === 'boolean')
        return String(value);
    return JSON.stringify(value);
}
function containsUrlControlCharacter(value) {
    for (let index = 0; index < value.length; index += 1) {
        const code = value.charCodeAt(index);
        if (code <= 31 || code === 127)
            return true;
    }
    return false;
}
function proxyTarget(input, rules) {
    let source;
    try {
        source = new URL(input);
    }
    catch {
        return input;
    }
    for (const rule of rules) {
        if (source.origin !== rule.origin || !rule.pathPrefixes.some((prefix) => source.pathname.startsWith(prefix))) {
            continue;
        }
        let endpoint;
        try {
            endpoint = new URL(rule.endpoint);
        }
        catch {
            continue;
        }
        const endpointPath = endpoint.pathname.endsWith('/') ? endpoint.pathname.slice(0, -1) : endpoint.pathname;
        endpoint.pathname = `${endpointPath}${source.pathname}`;
        endpoint.search = source.search;
        endpoint.hash = '';
        return endpoint.href;
    }
    return input;
}
function requestInput(options, environment, proxyRules) {
    const method = (options.method ?? 'GET').toUpperCase();
    if (options.url.trim() !== options.url || containsUrlControlCharacter(options.url)) {
        throw optionError('url contains surrounding whitespace or a control character');
    }
    let url;
    try {
        url = new URL(options.url, environment.baseUrl);
    }
    catch (error) {
        throw optionError(`url is invalid: ${error instanceof Error ? error.message : String(error)}`);
    }
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw optionError(`url protocol ${url.protocol || '<empty>'} is not supported`);
    }
    if (url.username.length > 0 || url.password.length > 0) {
        throw optionError('url must not contain embedded credentials');
    }
    const headers = requestHeaders(options.header);
    let body;
    if ((method === 'GET' || method === 'HEAD') && options.data !== undefined) {
        if (!isPlainRecord(options.data))
            throw optionError(`${method} data must be a plain object`);
        for (const [name, value] of Object.entries(options.data))
            url.searchParams.append(name, queryValue(value));
    }
    else if (options.data !== undefined) {
        if (typeof options.data === 'string' || options.data instanceof ArrayBuffer) {
            body = options.data;
        }
        else if (isPlainRecord(options.data) || Array.isArray(options.data)) {
            const contentType = headers.get('content-type')?.toLowerCase() ?? 'application/json';
            if (contentType.includes('application/x-www-form-urlencoded')) {
                if (!isPlainRecord(options.data))
                    throw optionError('form data must be a plain object');
                body = new URLSearchParams(Object.entries(options.data).map(([name, value]) => [name, queryValue(value)]));
            }
            else {
                if (!headers.has('content-type'))
                    headers.set('content-type', 'application/json');
                body = JSON.stringify(options.data);
            }
        }
        else {
            throw optionError('data must be a string, ArrayBuffer, array, or plain object');
        }
    }
    return {
        input: proxyTarget(url.href, proxyRules),
        init: {
            method,
            headers,
            ...(body === undefined ? {} : { body }),
        },
    };
}
function responseHeaders(response) {
    const result = {};
    response.headers.forEach((value, name) => { result[name] = value; });
    return Object.freeze(result);
}
async function responseData(response, options) {
    if (options.responseType === 'arraybuffer')
        return response.arrayBuffer();
    const text = await response.text();
    if ((options.dataType ?? 'json') === 'text')
        return text;
    if (text.length === 0)
        return null;
    try {
        return JSON.parse(text);
    }
    catch {
        return text;
    }
}
function invokeCallback(callback, value, kind, report) {
    if (callback === undefined)
        return;
    try {
        callback(value);
    }
    catch (error) {
        if (report !== undefined) {
            try {
                report(error, 'request', kind);
                return;
            }
            catch (reportError) {
                queueMicrotask(() => { throw reportError; });
                return;
            }
        }
        queueMicrotask(() => { throw error; });
    }
}
export function createBrowserRequestWxApis(options = {}) {
    if (options.onCallbackError !== undefined && typeof options.onCallbackError !== 'function') {
        throw new TypeError('onCallbackError must be a function when provided');
    }
    const proxyRules = Object.freeze([...(options.proxyRules ?? [])]);
    for (const rule of proxyRules) {
        if (typeof rule.origin !== 'string'
            || typeof rule.endpoint !== 'string'
            || !Array.isArray(rule.pathPrefixes)
            || rule.pathPrefixes.length === 0) {
            throw new TypeError('proxyRules must contain origin, endpoint, and pathPrefixes');
        }
    }
    const environment = options.environment ?? browserEnvironment();
    if (environment === null || typeof environment.fetch !== 'function' || typeof environment.baseUrl !== 'string') {
        throw new TypeError('createBrowserRequestWxApis requires a browser fetch environment');
    }
    return Object.freeze({
        request(raw) {
            const parsed = readOptions(raw);
            const prepared = requestInput(parsed, environment, proxyRules);
            const controller = new AbortController();
            const listeners = new Set();
            let timer;
            const task = Object.freeze({
                abort() {
                    controller.abort();
                },
                onHeadersReceived(listener) {
                    if (typeof listener !== 'function')
                        throw optionError('header listener must be a function');
                    listeners.add(listener);
                },
                offHeadersReceived(listener) {
                    if (listener === undefined)
                        listeners.clear();
                    else if (typeof listener !== 'function')
                        throw optionError('header listener must be a function');
                    else
                        listeners.delete(listener);
                },
            });
            void Promise.resolve().then(async () => {
                if ((parsed.timeout ?? 0) > 0)
                    timer = setTimeout(() => controller.abort(), parsed.timeout);
                const response = await environment.fetch(prepared.input, { ...prepared.init, signal: controller.signal });
                const header = responseHeaders(response);
                const headerResult = Object.freeze({ header });
                for (const listener of listeners)
                    listener(headerResult);
                const result = Object.freeze({
                    cookies: Object.freeze([]),
                    data: await responseData(response, parsed),
                    errMsg: 'request:ok',
                    header,
                    statusCode: response.status,
                });
                invokeCallback(parsed.success, result, 'success', options.onCallbackError);
                invokeCallback(parsed.complete, result, 'complete', options.onCallbackError);
            }).catch((error) => {
                const failure = requestError(error);
                invokeCallback(parsed.fail, failure, 'fail', options.onCallbackError);
                invokeCallback(parsed.complete, failure, 'complete', options.onCallbackError);
            }).finally(() => {
                if (timer !== undefined)
                    clearTimeout(timer);
            });
            return task;
        },
    });
}
//# sourceMappingURL=request-adapter.js.map